// SmartArbiSwap.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputSwitch } from "primereact/inputswitch";
import { InputNumber } from "primereact/inputnumber";
import { Slider } from "primereact/slider";
import Web3 from "web3";
import BigNumber from "bignumber.js";
import routerAbi from "../../contracts/routerAbi";
import erc20Abi from "../../contracts/ierc20Abi";
import { toast } from "react-toastify";
import {
  formatCurrencyExtended,
  formatDecimals,
  formatPercentage
} from "../../utils";

/* ─────────────────────────────────────────────────────────── */

BigNumber.config({ DECIMAL_PLACES: 40, EXPONENTIAL_AT: 1e9 });

const RPC         = "https://binance.llamarpc.com";
const web3        = new Web3(RPC);
const WBNB        = "0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c";
const ROUTER_ADDR = "0x10ED43C718714eb63d5aA57B78B54704E256024E";
const ROUTER      = new web3.eth.Contract(routerAbi, ROUTER_ADDR);
const DEADLINE    = 60 * 15; // seconds
const SLIPPAGE_DEFAULT = 5;
const GAS_BUFFER      = new BigNumber(0.0025);

export default function SmartArbiSwap({
  token,               // { address, symbol, decimals, price, balanceOfHolder }
  bnbBalance,          // wei string
  bnbPrice,            // USD price
  walletAddress,
  getLiquidityPairs,   // async fn(token) => [ { token, backingToken, tokenSymbol, backingTokenSymbol, router }, … ]
  getTokenLogoUrl      // fn({address,symbol}) => url
}) {
  /* ───────────────── UI state ───────────────── */
  const [visible,     setVisible]   = useState(false);
  const [startWithBNB,setStartWithBNB] = useState(true);
  const [amountStr,   setAmountStr]  = useState("");
  const [slippage,    setSlippage]   = useState(SLIPPAGE_DEFAULT);
  const [scanning,    setScanning]   = useState(false);
  const [processing,  setProcessing] = useState(false);

  const [bestRoute,   setBestRoute]  = useState([]);    // [ addr1, addr2, …, addr1 ]
  const [bestProfit,  setBestProfit] = useState(new BigNumber(0)); // in startAsset units

  /* ───────────────── Helpers ───────────────────── */
  const tokenDecimals = token?.decimals ?? 18;
  const bnInput       = useMemo(() => new BigNumber(amountStr||0), [amountStr]);

  // start asset metadata
  const startAddr     = startWithBNB ? WBNB : token.address;
  const startDecimals = startWithBNB ? 18   : tokenDecimals;
  const startLogo     = startWithBNB
    ? getTokenLogoUrl({address:WBNB,symbol:"BNB"})
    : getTokenLogoUrl({address:token.address,symbol:token.symbol});
  const startSymbol   = startWithBNB ? "BNB" : token.symbol;
  const startPrice    = startWithBNB ? bnbPrice    : (token.price||0);

  // human balances
  const bnbBalBN = useMemo(()=>new BigNumber(bnbBalance||0),[bnbBalance]);
  const tokenBalBN = useMemo(()=>new BigNumber(token.balanceOfHolder||0),[token]);
  const startBalRaw = startWithBNB
    ? bnbBalBN.div(1e18)
    : tokenBalBN.div(new BigNumber(10).pow(tokenDecimals));
  const startBalHuman = formatDecimals(startBalRaw, 6);
  const startBalUSD   = startBalRaw.times(startPrice);

  // cache liquidity calls
  const pairCache = useMemo(()=> ({}), []);
  const fetchPairs = useCallback(async (addr) => {
    const key = addr.toLowerCase();
    if (!pairCache[key]) {
      pairCache[key] = await getLiquidityPairs({address:addr});
    }
    return pairCache[key];
  }, [getLiquidityPairs, pairCache]);

  // convert user input to raw
  const amountRaw = useMemo(()=>{
    if (bnInput.isZero()) return "0";
    if (startWithBNB) {
      return web3.utils.toWei(bnInput.toFixed(18), "ether");
    } else {
      return bnInput.times(new BigNumber(10).pow(tokenDecimals)).toFixed(0);
    }
  }, [bnInput, startWithBNB, tokenDecimals]);

  /* ─────────────────── Scan Logic ───────────────────── */
  const scanArbitrage = useCallback(async ()=>{
    try {
      if (bnInput.lte(0) || !walletAddress) return;
      setScanning(true);
      setBestRoute([]);
      setBestProfit(new BigNumber(0));

      // we'll look for triangular loops of length 3 or 4
      // route must start+end at startAddr
      // build first-hop
      const hop1 = await fetchPairs(startAddr);

      // iterate hop1 → hop2 → hop3 → (optional hop4)
      let bestGain = new BigNumber(0);
      let winner  = null;

      for (const p1 of hop1) {
        const mid1 = p1.backingToken;
        const hop2  = await fetchPairs(mid1);

        for (const p2 of hop2) {
          const mid2 = p2.backingToken;
          const hop3 = await fetchPairs(mid2);

          // triangular: start→mid1→mid2→start
          const tri = [ startAddr, mid1, mid2, startAddr ];
          try {
            const amts = await ROUTER.methods.getAmountsOut(amountRaw, tri).call();
            const outRaw = new BigNumber(amts[amts.length-1]);
            const gain   = outRaw.minus(new BigNumber(amountRaw));
            if (gain.gt(0)) {
              const gainPct = gain.div(new BigNumber(amountRaw));
              if (gainPct.gt(bestGain)) {
                bestGain = gainPct;
                winner  = tri;
              }
            }
          } catch{ /* skip failures */ }

          // optional 4-hop: start→mid1→mid2→mid3→start
          for (const p3 of hop3) {
            const mid3 = p3.backingToken;
            const quad = [startAddr, mid1, mid2, mid3, startAddr];
            try {
              const amts = await ROUTER.methods.getAmountsOut(amountRaw, quad).call();
              const outRaw = new BigNumber(amts[amts.length-1]);
              const gain   = outRaw.minus(new BigNumber(amountRaw));
              if (gain.gt(0)) {
                const gainPct = gain.div(new BigNumber(amountRaw));
                if (gainPct.gt(bestGain)) {
                  bestGain = gainPct;
                  winner  = quad;
                }
              }
            } catch{}
          }
        }
      }

      if (winner) {
        setBestRoute(winner);
        setBestProfit(bestGain);
      } else {
        toast.info("No profitable arbitrage found");
      }
    } catch(err) {
      console.error(err);
      toast.error("Scan failed: "+err.message);
    } finally {
      setScanning(false);
    }
  },[
    amountRaw, bnInput, fetchPairs, startAddr,
    token.address, tokenDecimals, walletAddress
  ]);

  /* ───────────────── Execute Logic ─────────────────── */
  const executeArbitrage = async ()=>{
    if (!bestRoute.length) return;
    setProcessing(true);
    try {
      // here you'd loop through bestRoute and call the
      // appropriate swapExactTokensForTokens or swapExactETHForTokens etc.
      // with your slippage setting, from/to addresses, deadlines, etc.
      // For brevity we'll just toast:
      toast.info(
        `Executing arbitrage: ${bestRoute.join(" → ")}`
      );
      // … your actual swap calls here …
      toast.success("Arbitrage tx(s) submitted");
    } catch(err) {
      console.error(err);
      toast.error("Execute failed: "+err.message);
    } finally {
      setProcessing(false);
    }
  };

  /* ───────────────── UI rendering ─────────────────── */
  // route human display with logos
  const routeWithMeta = useMemo(()=>{
    return bestRoute.map(addr=>{
      const sym = addr.toLowerCase()===WBNB.toLowerCase()
                  ? "BNB"
                  : addr.toLowerCase()===token.address.toLowerCase()
                    ? token.symbol
                    : "";
      const logo= getTokenLogoUrl({address:addr,symbol:sym});
      return { address:addr, symbol: sym, logo };
    });
  },[bestRoute, token, getTokenLogoUrl]);

  const profitPct = bestProfit.isZero()
    ? null
    : formatPercentage(bestProfit.toNumber());
  const profitRaw = bestProfit.times(bnInput).toFixed(6);
  const profitUSD = formatCurrencyExtended(
    new BigNumber(profitRaw).times(startPrice)
  );

  return (
    <>
      <Button
        label="SmartArbiSwap"
        className="p-button-primary p-button-sm"
        onClick={()=>setVisible(true)}
      />

      <Dialog
        header="Smart Arbitrage Scanner"
        visible={visible}
        style={{ width: 480 }}
        onHide={()=>!processing && setVisible(false)}
        footer={
          <div style={{ textAlign:"right" }}>
            <Button
              label={bestRoute.length
                      ? `Execute (+${profitPct})`
                      : scanning
                        ? "Scanning…"
                        : "Scan"}
              icon={processing ? "pi pi-spin pi-spinner" : undefined}
              className={
                bestRoute.length
                  ? "p-button-success"
                  : "p-button-outlined"
              }
              loading={scanning||processing}
              disabled={scanning||processing}
              onClick={()=>
                bestRoute.length
                  ? executeArbitrage()
                  : scanArbitrage()
              }
            />
          </div>
        }
      >

        {/* start‐asset toggle */}
        <div style={{ display:"flex", alignItems:"center", marginBottom:12 }}>
          <span style={{flex:1}}>Start with BNB / {token.symbol}</span>
          <InputSwitch
            checked={startWithBNB}
            onChange={e=>{ setStartWithBNB(e.value); setBestRoute([]); }}
            tooltip="Toggle start asset"
          />
        </div>

        {/* amount input */}
        <div className="p-field" style={{marginBottom:16}}>
          <label style={{fontWeight:500}}>
            Amount ({startSymbol}) &nbsp;
            <span style={{color:"#666",fontSize:".85em"}}>
              ≈ {formatCurrencyExtended(bnInput.times(startPrice))}
            </span>
          </label>
          <InputNumber
            value={amountStr}
            mode="decimal"
            onChange={e=>setAmountStr(e.value)}
            minFractionDigits={0}
            maxFractionDigits={startDecimals}
            placeholder="0.0"
            disabled={scanning||processing}
            style={{width:"100%"}}
          />
          <div style={{
            marginTop:6, fontSize:".85em", color:"#666"
          }}>
            Balance: {startBalHuman} {startSymbol} (
            {formatCurrencyExtended(startBalUSD)})
          </div>
        </div>

        {/* slippage */}
        <div style={{marginBottom:16}}>
          <label>Slippage {slippage}%</label>
          <Slider
            value={slippage}
            onChange={e=>setSlippage(e.value)}
            min={0.5} max={20} step={0.5}
          />
        </div>

        {/* result */}
        {bestRoute.length>0 && (
          <div style={{fontSize:".9rem", lineHeight:1.4}}>
            <div style={{marginBottom:8}}>
              <strong>Estimated Profit:</strong><br/>
              {profitRaw} {startSymbol} (+{profitPct}) ≈ {profitUSD}
            </div>

            <div style={{
              display:"flex",
              alignItems:"center",
              background:"#222",
              color:"#fff",
              padding:"8px",
              borderRadius:4
            }}>
              <span style={{marginRight:12,whiteSpace:"nowrap"}}>
                Route:
              </span>
              <div style={{
                flex:1,
                display:"flex",
                justifyContent:"center",
                gap:"6px",
                flexWrap:"wrap"
              }}>
                {routeWithMeta.map((p,i)=>(
                  <React.Fragment key={p.address}>
                    <img src={p.logo} width={16} height={16}
                         style={{verticalAlign:"-2px"}}/>
                    {p.symbol}
                    {i<routeWithMeta.length-1 && "→"}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        )}

        {!bestRoute.length && !scanning && (
          <div style={{color:"#999",textAlign:"center",marginTop:24}}>
            Enter an amount & click “Scan” to discover arbitrage loops.
          </div>
        )}
      </Dialog>
    </>
  );
}

SmartArbiSwap.displayName = "SmartArbiSwap";
